import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../contexts/Auth'
import { useCookieUser } from './cookieUser'
import { DocumentState, ReferencesInfo, UserWriterSelections } from '../redux/types'
import {
	selectDocumentState,
	setContent,
	setIsGenerating,
	setIsGeneratingSubtopics,
	setIsHumanizing,
	setIsOnSubtopicsStep,
	setIsTypingEssay,
	setNeedsHumanizing,
	setTitle,
	setUserWriterSelections,
	setSetup,
	setSetupChanged,
	setReferences,
	createNewDraft,
	updateDraft,
	setHumanized,
	setAllReferences,
	setGeneratingReferences,
} from '../redux/docSlice'
import { CONSTANTS, DOC_TEMPLATES, HUMANIZER_OPTIONS } from '../constants'
import { useEffect } from 'react'
import { selectUser, selectUserPagesCache, setUserPagesCache } from '../redux/systemSlice'
import * as Sentry from '@sentry/react'
import { FEATURE_FLAGS, useFreeWordLimit } from './featureFlags'
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react'
import { htmlToStringBesidesH6 } from '../helpers/utility'
import useIsPremium from './useIsPremium'
import useNumSubtopics from './numSubtopics'
import { splitHTMLInto500WordChunks } from './helpers'
import { atom, useAtom } from 'jotai'
import { humanizedTextResultToDrafts } from '../Pages/Humanizer/helpers'
import { HumanizerIntensityLevel } from '../Pages/Humanizer/constants'

export const needsEssayGenerationAtom = atom(false)

const useWriteEssay = ({
	docID,
	type,
	topicValue,
	setTopicValue,
	setLocalUserWriterSelections,
	setErrorModalOpen,
	allowEssayGeneration = false,
}: {
	type?: keyof typeof DOC_TEMPLATES | undefined
	docID: string
	topicValue?: string
	setTopicValue?: (value: string) => void
	setLocalUserWriterSelections?: (value: UserWriterSelections) => void
	setErrorModalOpen?: (value: boolean) => void
	allowEssayGeneration?: boolean
}) => {
	const freeWordLimit = useFreeWordLimit()
	const { userIDCookie } = useCookieUser()
	const { currentUser } = useAuth() as any
	const dispatch = useDispatch()
	const documentState: DocumentState = useSelector((state) => selectDocumentState(state, docID))
	const user = useSelector(selectUser)
	const humanizerV4 = useFeatureFlagVariantKey(FEATURE_FLAGS.HUMANIZER_V4)
	const shortWriterV2 = useFeatureFlagVariantKey(FEATURE_FLAGS.SHORT_WRITER_V2)
	const sentenceHumanizerFF = useFeatureFlagVariantKey(FEATURE_FLAGS.SENTENCE_HUMANIZER)
	const posthog = usePostHog()
	const isPremium = useIsPremium()
	const userPagesCache = useSelector(selectUserPagesCache)
	const { averageNumberOfSubtopics } = useNumSubtopics()
	const [needsEssayGeneration, setNeedsEssayGeneration] = useAtom(needsEssayGenerationAtom)
	const sentenceReferenceFF = useFeatureFlagVariantKey(FEATURE_FLAGS.SENTENCE_REFERENCE)
	const asyncShortEssayFF = useFeatureFlagVariantKey(FEATURE_FLAGS.ASYNC_SHORT_ESSAY)
	const maskHumanizerFF = useFeatureFlagVariantKey(FEATURE_FLAGS.MASK_HUMANIZER)
	const topicValueLocal = topicValue ?? documentState?.title

	useEffect(() => {
		if (allowEssayGeneration && needsEssayGeneration && !documentState.isGenerating) {
			setLocalUserWriterSelections?.(documentState.userWriterSelections)
			dispatch(setIsGenerating({ docID: docID, isGenerating: true }))
			dispatch(setSetup({ docID: docID, setup: true }))
			dispatch(setSetupChanged({ docID: docID, setupChanged: true }))
			dispatch(setTitle({ docID: docID, title: topicValueLocal }))
		}

		if (
			allowEssayGeneration &&
			needsEssayGeneration &&
			(!documentState.generatingReferences || !documentState.userWriterSelections.refStyle)
		) {
			setNeedsEssayGeneration(false)
			generateContent()
		}

		if (
			sentenceReferenceFF === 'test' &&
			!documentState?.userWriterSelections?.refStyle &&
			!!documentState?.setup &&
			!needsEssayGeneration &&
			!documentState.generatingReferences
		) {
			dispatch(setAllReferences({ docID: docID, allReferences: [] }))
			dispatch(setReferences({ docID: docID, references: [] }))
		}
	}, [
		needsEssayGeneration,
		topicValueLocal,
		documentState.generatingReferences,
		documentState.userWriterSelections.refStyle,
		documentState?.setup,
		allowEssayGeneration,
		sentenceReferenceFF,
	])

	useEffect(() => {
		if (documentState.drafts.length === 2) {
			dispatch(setIsGenerating({ docID: docID, isGenerating: false }))
		}
	}, [documentState.drafts])

	useEffect(() => {
		setTopicValue?.(documentState?.title ?? '')
	}, [documentState?.title])

	const shortenEssayMaxFreeWords = (essay: string) => {
		const essayArray = essay.split('.')
		let newEssay = ''
		let wordCount = 0
		while (wordCount < freeWordLimit && essayArray.length !== 0) {
			wordCount += essayArray[0].split(' ').length
			if (essayArray.length !== 1) {
				newEssay += essayArray.shift() + '.'
			}
		}
		return newEssay
	}

	async function updateHumanized(humanized: boolean) {
		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
			},
			body: JSON.stringify({
				id: docID,
				humanized: humanized,
			}),
		}
		fetch(process.env.REACT_APP_API_ROOT + '/document/update/', requestOptions)
	}

	async function makeHumanizerRequest(essayContent: string, humanizerIntensity?: string) {
		// Use new mask-humanize API when feature flag is enabled
		if (maskHumanizerFF === 'test') {
			return makeMaskHumanizerRequest(essayContent)
		}
		return makeLegacyHumanizerRequest(essayContent, humanizerIntensity)
	}

	async function makeMaskHumanizerRequest(essayContent: string) {
		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
			},
			body: JSON.stringify({
				text: htmlToStringBesidesH6(essayContent),
			}),
		}

		try {
			// Submit the humanization job
			const submitResponse = await fetch(process.env.REACT_APP_API_ROOT + '/essay/mask-humanize/', requestOptions)
			const submitResult = await submitResponse.json()
			const jobId = submitResult.jobId

			if (!jobId) {
				throw new Error('No jobId returned from humanize submit')
			}

			// Poll for the result
			const pollForResult = async (): Promise<any> => {
				const pollResponse = await fetch(process.env.REACT_APP_API_ROOT + `/essay/mask-humanize/jobs/${jobId}/`, {
					method: 'GET',
					headers: { 'Content-Type': 'application/json' },
				})
				const pollResult = await pollResponse.json()

				if (pollResult.result !== undefined) {
					// Job is complete, convert plain text to HTML paragraphs for TipTap
					const htmlContent = pollResult.result
						.split('\n')
						.filter((line: string) => line.trim() !== '')
						.map((line: string) => `<p>${line}</p>`)
						.join('')
					return { content: htmlContent }
				}

				// Job not complete, wait and poll again
				await new Promise((resolve) => setTimeout(resolve, 1000))
				return pollForResult()
			}

			return await pollForResult()
		} catch (e) {
			Sentry.captureException(e)
			setErrorModalOpen?.(true)
			return ''
		}
	}

	async function makeLegacyHumanizerRequest(essayContent: string, humanizerIntensity?: string) {
		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
			},
			body: JSON.stringify({
				text: htmlToStringBesidesH6(essayContent),
				humanizerIntensity: !isPremium
					? HumanizerIntensityLevel.LOW
					: humanizerIntensity ?? documentState.userWriterSelections?.humanizerIntensity,
				token: currentUser ? await currentUser.getIdToken() : undefined,
				userID: user.id ?? userIDCookie,
				useHumanizerV4: humanizerV4 === 'test' ? true : false,
				singleDraft: true,
				plainText: false,
			}),
		}

		try {
			// Await the fetch call and its conversion to JSON
			const response = await fetch(
				process.env.REACT_APP_API_ROOT +
					(sentenceHumanizerFF === 'test' ? '/essay/humanize-sentence-free/' : '/essay/humanize/'),
				requestOptions
			)
			const result = await response.json()
			return result
		} catch (e) {
			Sentry.captureException(e)
			setErrorModalOpen?.(true)
			// Ensure that in case of error, the function returns a fallback or error indicator
			return '' // Consider returning null or a specific error object depending on your handling logic
		}
	}

	async function humanizeEssay(essayContent: string, draftIdx: number, humanizerIntensity?: string) {
		dispatch(setIsHumanizing({ docID: docID, isHumanizing: true }))

		let humanizedContent = ''

		// Mask humanizer handles content directly
		if (maskHumanizerFF === 'test') {
			const chunks: string[] = splitHTMLInto500WordChunks(essayContent, 500)

			// Kick off all requests in parallel
			const promises = chunks.map((chunk) => makeHumanizerRequest(chunk, humanizerIntensity))

			// Await all results at once
			const results = await Promise.all(promises)

			// Combine the results
			for (const result of results) {
				if (result.content) {
					humanizedContent += result.content
				}
			}
		} else if (humanizerV4 === 'test' || sentenceHumanizerFF === 'test') {
			const chunks: string[] = splitHTMLInto500WordChunks(essayContent, 500)

			// Kick off all requests in parallel
			const promises = chunks.map((chunk) => makeHumanizerRequest(chunk, humanizerIntensity))

			// Await all results at once
			const results = await Promise.all(promises)

			// Combine the results
			for (const result of results) {
				if (sentenceHumanizerFF === 'test') {
					humanizedContent += humanizedTextResultToDrafts(result, true)[0]
				} else if (result.content) {
					humanizedContent += result.content
				}
			}
		} else {
			let chunks = []

			if (essayContent.length > 1250 * 5) {
				// Split the essay into two chunks based on newlines
				const splitContent = essayContent.split('\n')
				const mid = Math.floor(splitContent.length / 2)
				const firstHalf = splitContent.slice(0, mid).join('\n')
				const secondHalf = splitContent.slice(mid).join('\n')
				chunks = [firstHalf, secondHalf]
			} else {
				chunks = [essayContent]
			}

			for (let i = 0; i < chunks.length; i++) {
				const result = await makeHumanizerRequest(chunks[i], humanizerIntensity)
				if (result.content) {
					humanizedContent += result.content // Adjust this line based on the actual property
				}
			}
		}

		if (draftIdx !== -1) {
			dispatch(
				updateDraft({
					docID: docID,
					content: humanizedContent,
					draftIdx: draftIdx,
				})
			)
			dispatch(setContent({ docID: docID, content: humanizedContent }))
		} else {
			dispatch(setContent({ docID: docID, content: humanizedContent }))
		}
		dispatch(setIsGenerating({ docID: docID, isGenerating: false }))
		dispatch(setIsHumanizing({ docID: docID, isHumanizing: false }))
		dispatch(setIsTypingEssay({ docID: docID, isTypingEssay: false }))
		dispatch(setNeedsHumanizing({ docID: docID, needsHumanizing: false }))
		dispatch(setHumanized({ docID: docID, humanized: true }))
		updateHumanized(true)
	}

	async function writeOutline() {
		const requestBody = JSON.stringify({
			prompt: topicValueLocal,
			userId: user.id ?? userIDCookie,
			docId: docID,
			userWriterSelections: documentState.userWriterSelections,
		})

		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
			},
			body: requestBody,
		}

		const url = `${process['env']['REACT_APP_API_ROOT']}/essay/write-outline/`
		fetch(url, requestOptions)
			.then((res) => {
				dispatch(setIsTypingEssay({ docID: docID, isTypingEssay: true }))
				dispatch(setIsGenerating({ docID: docID, isGenerating: false }))
				return res.json()
			})
			.then(async (result: { essay: string }) => {
				dispatch(setContent({ docID: docID, content: result.essay.trim() }))
				dispatch(setIsTypingEssay({ docID: docID, isTypingEssay: false }))
			})
			.catch((e) => {
				Sentry.captureException(e)
				setErrorModalOpen?.(true)
				dispatch(setIsGenerating({ docID: docID, isGenerating: false }))
				dispatch(setIsTypingEssay({ docID: docID, isTypingEssay: false }))
				dispatch(setContent({ docID: docID, content: '' }))
			})
	}

	async function getSectionContent(
		topic: string,
		sectionType: 'introduction' | 'body' | 'conclusion',
		essayContent: string
	) {
		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				prompt: topicValueLocal,
				userId: user.id ?? userIDCookie,
				docId: docID,
				userWriterSelections: documentState.userWriterSelections,
				sectionType: sectionType,
				topic: topic,
				essay: essayContent,
				useNeuralSearch: false,
			}),
		}

		const url = `${process['env']['REACT_APP_API_ROOT']}/essay/write-section/`

		try {
			const response = await fetch(url, requestOptions)
			const result = await response.json()
			if (!!result.references && result.references.length > 0) {
				posthog.capture('references-generated', { neural: false })
				dispatch(
					setReferences({
						docID: docID,
						references: result.references,
					})
				)
			}
			return result.essay.trim()
		} catch (e) {
			Sentry.captureException(e)
			throw new Error('Failed to fetch section content')
		}
	}

	async function writeLongEssay(draftIdx: number) {
		let content = ''
		let { subTopics } = documentState.userWriterSelections
		subTopics = subTopics.filter((topic) => topic.trim() !== '')
		let shorteningEssay = false

		if (!isPremium) {
			shorteningEssay = true
			subTopics = freeWordLimit > 250 ? [subTopics[0], subTopics[1]] : [subTopics[0]]
		}

		try {
			// Generate all sections concurrently
			const sectionPromises = subTopics.map((topic, i) => {
				const sectionType =
					i === 0 ? 'introduction' : shorteningEssay ? 'body' : i === subTopics.length - 1 ? 'conclusion' : 'body'
				return getSectionContent(topic, sectionType, content)
			})
			const sections = await Promise.all(sectionPromises)
			content = sections.map((section) => section.trim()).join('')

			if (shorteningEssay) {
				content = shortenEssayMaxFreeWords(content)
			}

			// Dispatch the content and draft updates
			if (draftIdx === 0) {
				posthog.capture('essay-generated', {
					type: type,
				})
				dispatch(setContent({ docID: docID, content }))
				dispatch(setIsTypingEssay({ docID: docID, isTypingEssay: false }))
			}
			dispatch(
				createNewDraft({
					docID: docID,
					draft: { content, selected: draftIdx === 0 },
					idx: draftIdx,
				})
			)

			// Humanize if applicable
			if (documentState.userWriterSelections?.humanizerIntensity !== HUMANIZER_OPTIONS[0]) {
				await humanizeEssay(content.trim(), draftIdx)
			}
		} catch (e) {
			Sentry.captureException(e)
			setErrorModalOpen?.(true)
			dispatch(setIsGenerating({ docID: docID, isGenerating: false }))
			dispatch(setIsTypingEssay({ docID: docID, isTypingEssay: false }))
			dispatch(setContent({ docID: docID, content: '' }))
		}
	}

	async function writeShortEssay(draftIdx: number) {
		if (asyncShortEssayFF === 'test') {
			return writeShortEssayAsync(draftIdx)
		}

		const requestBody = JSON.stringify({
			prompt: topicValueLocal,
			userId: user.id ?? userIDCookie,
			docId: docID,
			userWriterSelections: documentState.userWriterSelections,
			useNeuralSearch: false,
			writerV2: shortWriterV2 === 'test',
		})

		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
			},
			body: requestBody,
		}

		const url = `${process['env']['REACT_APP_API_ROOT']}/essay/write-short-essay/`
		fetch(url, requestOptions)
			.then((res) => {
				return res.json()
			})
			.then(async (result: { essay: string; references: ReferencesInfo[] }) => {
				let essay = result.essay.trim()
				if (!isPremium && documentState.userWriterSelections.wordCount > freeWordLimit) {
					essay = shortenEssayMaxFreeWords(essay)
				}

				if (draftIdx === 0) {
					posthog.capture('essay-generated', {
						type: type,
					})
					dispatch(setContent({ docID: docID, content: essay }))
					dispatch(setIsTypingEssay({ docID: docID, isTypingEssay: false }))
				}
				dispatch(
					createNewDraft({
						docID: docID,
						draft: { content: essay, selected: draftIdx === 0 },
						idx: draftIdx,
					})
				)

				if (!!result.references && result.references.length > 0) {
					posthog.capture('references-generated', { neural: false })
					dispatch(
						setReferences({
							docID: docID,
							references: result.references,
						})
					)
				}
				if (documentState.userWriterSelections?.humanizerIntensity !== HUMANIZER_OPTIONS[0]) {
					await humanizeEssay(essay, draftIdx)
				}
			})
			.catch((e) => {
				Sentry.captureException(e)
				setErrorModalOpen?.(true)
				dispatch(setIsGenerating({ docID: docID, isGenerating: false }))
				dispatch(setIsTypingEssay({ docID: docID, isTypingEssay: false }))
				dispatch(setContent({ docID: docID, content: '' }))
			})
	}

	async function writeShortEssayAsync(draftIdx: number) {
		const requestBody = JSON.stringify({
			prompt: topicValueLocal,
			userId: user.id ?? userIDCookie,
			doc_id: docID,
			userWriterSelections: documentState.userWriterSelections,
			useNeuralSearch: false,
			writerV2: shortWriterV2 === 'test',
		})

		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
			},
			body: requestBody,
		}

		try {
			const url = `${process['env']['REACT_APP_API_ROOT']}/essay/write-short-essay-async/`
			const submitResponse = await fetch(url, requestOptions)
			const submitResult = await submitResponse.json()

			if (!submitResult.jobId) {
				throw new Error('No jobId returned from async short essay submit')
			}

			const pollForResult = async (): Promise<{ essay: string; references?: ReferencesInfo[] }> => {
				const pollResponse = await fetch(`${url}?job_id=${submitResult.jobId}`, {
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
					},
				})
				const pollResult = await pollResponse.json()

				if (pollResult.status === 'success') {
					return pollResult.result
				}

				if (pollResult.status === 'error') {
					throw new Error(pollResult.error || 'Async essay generation failed')
				}

				await new Promise((resolve) => setTimeout(resolve, 2000))
				return pollForResult()
			}

			const result = await pollForResult()
			let essay = result.essay.trim()
			if (!isPremium && documentState.userWriterSelections.wordCount > freeWordLimit) {
				essay = shortenEssayMaxFreeWords(essay)
			}

			if (draftIdx === 0) {
				posthog.capture('essay-generated', { type: type })
				dispatch(setContent({ docID: docID, content: essay }))
				dispatch(setIsTypingEssay({ docID: docID, isTypingEssay: false }))
			}
			dispatch(
				createNewDraft({
					docID: docID,
					draft: { content: essay, selected: draftIdx === 0 },
					idx: draftIdx,
				})
			)

			if (result.references && result.references.length > 0) {
				posthog.capture('references-generated', { neural: false })
				dispatch(
					setReferences({
						docID: docID,
						references: result.references,
					})
				)
			}
			if (documentState.userWriterSelections?.humanizerIntensity !== HUMANIZER_OPTIONS[0]) {
				await humanizeEssay(essay, draftIdx)
			}
		} catch (e) {
			Sentry.captureException(e)
			setErrorModalOpen?.(true)
			dispatch(setIsGenerating({ docID: docID, isGenerating: false }))
			dispatch(setIsTypingEssay({ docID: docID, isTypingEssay: false }))
			dispatch(setContent({ docID: docID, content: '' }))
		}
	}

	async function generateReferences() {
		dispatch(setGeneratingReferences({ docID: docID, generatingReferences: true }))
		const requestBody = JSON.stringify({
			prompt: topicValueLocal,
			userId: user.id ?? userIDCookie,
			docId: docID,
			wordCount: documentState.userWriterSelections.wordCount,
			autoExa: false,
		})

		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
			},
			body: requestBody,
		}

		const url = `${process['env']['REACT_APP_API_ROOT']}/essay/generate-references/`

		try {
			const res = await fetch(url, requestOptions)
			const result = await res.json()
			dispatch(setGeneratingReferences({ docID: docID, generatingReferences: false }))

			if (result.references && result.references.length > 0) {
				posthog.capture('references-generated', { neural: false })
				// Get existing custom references
				const existingCustomReferences = documentState?.references?.filter((ref) => ref.custom) ?? []

				let references = []
				let allReferences = []

				if (existingCustomReferences.length > 0) {
					// Combine custom references with new references
					references = [...existingCustomReferences]
					allReferences = [...existingCustomReferences, ...result.allReferences]
				} else {
					references = [...result.references]
					allReferences = [...result.allReferences]
				}

				dispatch(
					setReferences({
						docID: docID,
						references: references,
					})
				)
				dispatch(
					setAllReferences({
						docID: docID,
						allReferences: allReferences,
					})
				)
			}
		} catch (e) {
			console.log('error', e)
			Sentry.captureException(e)
			setErrorModalOpen?.(true)
			dispatch(setIsGenerating({ docID: docID, isGenerating: false }))
			dispatch(setIsTypingEssay({ docID: docID, isTypingEssay: false }))
			dispatch(setContent({ docID: docID, content: '' }))
		}
	}

	async function generateContent() {
		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
			},
			body: JSON.stringify({
				topic: topicValueLocal,
				id: docID,
			}),
		}

		fetch(process['env']['REACT_APP_API_ROOT'] + '/document/update/', requestOptions)
			.then((res) => res.json())
			.then(async (success: boolean) => {
				if (success) {
					if (type === DOC_TEMPLATES.Outline) {
						writeOutline()
					} else if (documentState.userWriterSelections?.wordCount >= CONSTANTS.TOPIC_GENERATION_BREAKPOINT) {
						writeLongEssay(0)
						writeLongEssay(1)
					} else {
						writeShortEssay(0)
						writeShortEssay(1)
					}
					if (type !== DOC_TEMPLATES.Outline) {
						dispatch(
							setHumanized({
								docID: docID,
								humanized: documentState.userWriterSelections.humanizerIntensity !== 'NONE',
							})
						)
						updateHumanized(documentState.userWriterSelections.humanizerIntensity !== 'NONE')
					}
					if (user.isOnFreeTrial) {
						dispatch(setUserPagesCache({ ...userPagesCache, dirty: true }))
					}
				} else {
					dispatch(setIsGenerating({ docID: docID, isGenerating: false }))
				}
			})
			.catch((e) => {
				Sentry.captureException(e)
				dispatch(setIsGenerating({ docID: docID, isGenerating: false }))
			})
	}

	const getSubtopics = async () => {
		dispatch(setIsGeneratingSubtopics({ docID: docID, isGeneratingSubtopics: true }))
		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
			},
			body: JSON.stringify({
				wordCount: documentState.userWriterSelections.wordCount,
				prompt: topicValueLocal,
				type: documentState.userWriterSelections.type,
				thesisStatement: documentState.userWriterSelections.thesisStatement.active
					? documentState.userWriterSelections.thesisStatement.text
					: '',
				userId: user.id ?? userIDCookie,
				numSubtopics: averageNumberOfSubtopics,
			}),
		}
		fetch(process['env']['REACT_APP_API_ROOT'] + '/essay/subtopics/', requestOptions)
			.then((res) => res.json())
			.then((response: { outline: string[] }) => {
				dispatch(
					setUserWriterSelections({
						docID: docID,
						userWriterSelections: {
							subTopics: response.outline,
						},
					})
				)
				dispatch(
					setIsOnSubtopicsStep({
						docID: docID,
						isOnSubtopicsStep: true,
					})
				)
				dispatch(setIsGeneratingSubtopics({ docID: docID, isGeneratingSubtopics: false }))
			})
			.catch((e) => {
				Sentry.captureException(e)
				dispatch(setIsGeneratingSubtopics({ docID: docID, isGeneratingSubtopics: false }))
			})
	}

	return {
		generateEssay: () => {
			setNeedsEssayGeneration(true)
		},
		getSubtopics,
		humanizeEssay,
		generateReferences,
	}
}

export default useWriteEssay
